export interface QueuedAttachment {
	id: string;
	file: File;
	previewUrl?: string;
}

export const MAX_CHAT_ATTACHMENTS = 4;
export const MAX_CHAT_ATTACHMENT_SIZE = 10 * 1024 * 1024;

const allowedAttachmentTypes: Record<string, string[]> = {
	'.png': ['image/png'],
	'.jpg': ['image/jpeg'],
	'.jpeg': ['image/jpeg'],
	'.gif': ['image/gif'],
	'.webp': ['image/webp'],
	'.pdf': ['application/pdf'],
	'.doc': ['application/msword'],
	'.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
	'.xls': ['application/vnd.ms-excel'],
	'.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
	'.txt': ['text/plain'],
	'.zip': ['application/zip', 'application/x-zip-compressed'],
};

export const isAllowedChatAttachment = (file: File) => {
	const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
	return file.size > 0 && file.size <= MAX_CHAT_ATTACHMENT_SIZE && allowedAttachmentTypes[extension]?.includes(file.type) === true;
};

export const isImageAttachment = (file: QueuedAttachment) => file.file.type.startsWith('image/');

export const formatAttachmentSize = (bytes: number) => {
	if (bytes < 1024 * 1024) {
		return `${Math.max(1, Math.round(bytes / 1024))} KB`;
	}

	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const releaseAttachmentPreview = (attachment: QueuedAttachment) => {
	if (attachment.previewUrl) {
		URL.revokeObjectURL(attachment.previewUrl);
	}
};

interface PresignedUpload {
	uploadUrl: string;
	fields: Record<string, string>;
	mimeType: string;
}

export const uploadAttachmentsToS3 = async (
	files: QueuedAttachment[],
	uploads: PresignedUpload[],
	request: typeof fetch = fetch
) => {
	if (files.length !== uploads.length) throw new Error('Upload metadata does not match selected files');
	await Promise.all(files.map(async (attachment, index) => {
		const upload = uploads[index];
		const body = new FormData();
		Object.entries(upload.fields).forEach(([key, value]) => body.append(key, value));
		body.append('file', attachment.file);
		const response = await request(upload.uploadUrl, {
			method: 'POST',
			body,
		});
		if (!response.ok) throw new Error(`S3 upload failed with status ${response.status}`);
	}));
};
